import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadIdentity } from "../lib/identity";
import { devDebug } from "../lib/devLog";

const API = process.env.REACT_APP_BACKEND_URL || "";
const FEED_SYNC_INTERVAL_MS = 1500;

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function relativeTime(iso) {
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return "adesso";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min fa`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} ore fa`;
  return `${Math.floor(diffSec / 86400)} giorni fa`;
}

export function sameIdentity(left, right) {
  const a = String(left || "").trim().toLowerCase();
  const b = String(right || "").trim().toLowerCase();
  return Boolean(a && b && a === b);
}

// Transform a backend post into the display shape used by <VoicePost/>.
export function toDisplayPost(bp, myIdentity) {
  const shortId = bp.identityId.slice(0, 6);
  const author = bp.displayName || `Utente ${shortId}`;
  const handle = bp.displayName
    ? `@${bp.displayName.toLowerCase().replace(/\s+/g, "")}`
    : `@${shortId}`;
  const avatar = bp.avatar || "/you-voice-logo.png";
  const audioUrl = bp.audioObjectKey
    ? `${API}/api/v1/uploads/audio/${bp.audioObjectKey}`
    : null;
  const myIdentityId = typeof myIdentity === "string"
    ? myIdentity
    : myIdentity?.identityId;
  const myPublicKeyHex = typeof myIdentity === "object"
    ? myIdentity?.publicKeyHex
    : null;
  const isMine = sameIdentity(bp.identityId, myIdentityId)
    || sameIdentity(bp.publicKeyHex, myPublicKeyHex);
  return {
    id: bp.id,
    author,
    handle,
    avatar,
    identityId: bp.identityId,
    time: relativeTime(bp.publishedAt),
    verified: false,
    text: bp.caption || "Nuovo messaggio vocale pubblicato.",
    attachments: Array.isArray(bp.attachments) ? bp.attachments : [],
    duration: formatDuration(bp.durationSeconds),
    audioObjectKey: bp.audioObjectKey || null,
    audioUrl,
    followedByMe: !!bp.followedByMe,
    isMine,
    replies: bp.replies || 0,
    reactions: bp.reactions || { like: 0, dislike: 0 },
    source: "live",
  };
}

/**
 * Loads real posts for a topic from the backend.
 * An empty API result remains empty: production must never invent users or content.
 * When `followedOnly` is true, requests only posts from followed identities.
 *
 * Visible feeds are synchronized in the background so creations, deletions and
 * moderation changes propagate to every open browser without a manual refresh.
 */
export function useFeedPosts(topicSlug, followedOnly = false) {
  const [livePosts, setLivePosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const refreshInFlight = useRef(false);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!topicSlug || refreshInFlight.current) return;
    refreshInFlight.current = true;
    if (!silent) setLoading(true);
    const me = loadIdentity();
    const url = followedOnly && me?.identityId
      ? `${API}/api/v1/posts/by-topic/${encodeURIComponent(topicSlug)}?followedBy=${me.identityId}`
      : `${API}/api/v1/posts/by-topic/${encodeURIComponent(topicSlug)}`;
    try {
      const r = await axios.get(url, {
        headers: me?.identityId ? { "x-ol-identity": me.identityId } : {},
        timeout: 8000,
      });
      setLivePosts((r.data?.items || []).map((bp) => toDisplayPost(bp, me)));
    } catch (err) {
      if (!silent) setLivePosts([]);
      devDebug("useFeedPosts: fetch failed", err);
    } finally {
      refreshInFlight.current = false;
      if (!silent) setLoading(false);
    }
  }, [topicSlug, followedOnly]);

  useEffect(() => {
    void refresh();

    const synchronizeVisibleFeed = () => {
      if (document.visibilityState === "visible") {
        void refresh({ silent: true });
      }
    };
    const intervalId = window.setInterval(
      synchronizeVisibleFeed,
      FEED_SYNC_INTERVAL_MS,
    );
    document.addEventListener("visibilitychange", synchronizeVisibleFeed);
    window.addEventListener("focus", synchronizeVisibleFeed);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", synchronizeVisibleFeed);
      window.removeEventListener("focus", synchronizeVisibleFeed);
    };
  }, [refresh]);

  return {
    posts: livePosts,
    livePosts,
    loading,
    refresh,
  };
}
